import { useMemo } from "react";
import { useRBAC } from "./hooks";
import {
  sidebarMenuSections,
  type SidebarMenuGroup,
  type SidebarMenuItem,
  type SidebarMenuSection,
} from "./sidebar-config";

export interface VisibleNavGroup extends SidebarMenuGroup {
  /** Defined with a single item in config → rendered as a direct link. */
  isSingle: boolean;
}

export interface VisibleNavSection extends Omit<SidebarMenuSection, "groups"> {
  groups: VisibleNavGroup[];
}

export interface NavMatch {
  section: VisibleNavSection;
  group: VisibleNavGroup;
  item: SidebarMenuItem;
  /** False when the route is a child of the item (e.g. a detail page). */
  isExact: boolean;
}

/**
 * Sidebar sections filtered by the user's RBAC permissions, with empty
 * groups / sections dropped. Shared by the sidebar, header breadcrumbs and
 * the command palette so they always agree.
 */
export function useVisibleNav(): VisibleNavSection[] {
  const { hasPermission, isLoading } = useRBAC();

  return useMemo(() => {
    const canSee = (item: SidebarMenuItem) =>
      isLoading || !item.permission || hasPermission(item.permission);

    return sidebarMenuSections
      .map((section) => ({
        ...section,
        groups: section.groups
          .map((group) => ({
            ...group,
            isSingle: group.items.length === 1,
            items: group.items.filter(canSee),
          }))
          .filter((group) => group.items.length > 0),
      }))
      .filter((section) => section.groups.length > 0);
  }, [hasPermission, isLoading]);
}

/** The most specific nav item whose path is the route or an ancestor of it. */
export function findNavMatch(
  pathname: string,
  sections: VisibleNavSection[]
): NavMatch | null {
  let best: NavMatch | null = null;
  for (const section of sections) {
    for (const group of section.groups) {
      for (const item of group.items) {
        const isExact = pathname === item.path;
        const isAncestor = item.path !== "/" && pathname.startsWith(`${item.path}/`);
        if (!isExact && !isAncestor) continue;
        if (!best || item.path.length > best.item.path.length) {
          best = { section, group, item, isExact };
        }
      }
    }
  }
  return best;
}
